import * as React from 'react';

/**
 * How much of the next slide stays visible at the right edge.
 * `none` keeps today's even fractions. The other values are space steps.
 */
export const CAROUSEL_NEXT_PEEK = ['none', 'sm', 'md', 'lg'] as const;

export type CarouselNextPeek = (typeof CAROUSEL_NEXT_PEEK)[number];

/** Set on the carousel when a peek size is actually applied. */
export const CAROUSEL_NEXT_PEEK_VARIABLE = '--carousel-next-peek';

const PEEK_ROOT_CLASS: Record<Exclude<CarouselNextPeek, 'none'>, string> = {
  sm: '[--carousel-next-peek:var(--space-sm)]',
  md: '[--carousel-next-peek:var(--space-md)]',
  lg: '[--carousel-next-peek:var(--space-lg)]',
};

const FRACTION_COUNT = {
  half: 2,
  third: 3,
  quarter: 4,
  fifth: 5,
} as const;

type FractionBasis = keyof typeof FRACTION_COUNT;

/**
 * `half` is one full slide until the md breakpoint, then two-up.
 * The peek class follows that same breakpoint, unless the caller asks
 * for the phone slice. That slice is one slide minus the peek, and the
 * tablet class still takes over from md.
 */
/**
 * `100%` is the track, which is one `pl-4` gutter wider than the viewport.
 * That same gutter is empty padding at the start of the next slide.
 * `--carousel-slide-gutter` is `1rem` unless a layout has already removed
 * the padding. The `0px` fallback keeps a missing peek variable from
 * making the basis invalid.
 */
/** One half-width slide on a phone, short by the peek, when the caller asks. */
const PHONE_HALF_PEEK_CLASS =
  'basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/1)]';

const BASE_PEEK_CLASS: Record<FractionBasis, string> = {
  half: 'md:basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/2)]',
  third:
    'basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/3)]',
  quarter:
    'basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/4)]',
  fifth:
    'basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/5)]',
};

const MD_PEEK_CLASS: Record<FractionBasis, string> = {
  half: 'md:basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/2)]',
  third:
    'md:basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/3)]',
  quarter:
    'md:basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/4)]',
  fifth:
    'md:basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/5)]',
};

const LG_PEEK_CLASS: Record<FractionBasis, string> = {
  half: 'lg:basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/2)]',
  third:
    'lg:basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/3)]',
  quarter:
    'lg:basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/4)]',
  fifth:
    'lg:basis-[calc((100%-var(--carousel-slide-gutter,1rem)-var(--carousel-next-peek,0px))/5)]',
};

/**
 * Accept a catalog or runtime value. Anything other than a size is `none`.
 *
 * @param value - Author or caller input
 * @returns A public peek value
 */
export function normalizeCarouselNextPeek(value: unknown): CarouselNextPeek {
  if (value === 'sm' || value === 'md' || value === 'lg') return value;
  return 'none';
}

/**
 * Class that sets the peek length. Omitted when the peek is not applied.
 *
 * @param peek - Normalized peek value
 * @returns Tailwind arbitrary-property class, or undefined
 */
export function carouselNextPeekRootClass(
  peek: CarouselNextPeek,
): string | undefined {
  if (peek === 'none') return undefined;
  return PEEK_ROOT_CLASS[peek];
}

/**
 * The peek is off while the carousel loops. Grouped paging already forces
 * looping off, so a size applies there even when `loop` was omitted.
 *
 * @param input.peek - Normalized peek value
 * @param input.loop - Caller loop flag. Omitted means the carousel loops.
 * @param input.paging - `group` turns looping off
 * @returns Whether a size should change layout
 */
export function isCarouselNextPeekEnabled(input: {
  peek: CarouselNextPeek;
  loop: boolean | undefined;
  paging: 'slide' | 'group';
}): boolean {
  if (input.peek === 'none') return false;
  if (input.paging === 'group') return true;
  return input.loop === false;
}

const SLIDE_DISPLAY_NAME = 'CarouselItem';

type PeekSlideProps = {
  basis?: unknown;
  'basis-md'?: unknown;
  'basis-lg'?: unknown;
};

function readSlideProps(
  child: React.ReactElement,
): PeekSlideProps & { children?: React.ReactNode } {
  return child.props as PeekSlideProps & { children?: React.ReactNode };
}

function isCarouselItemType(type: unknown): boolean {
  if (
    type === null ||
    (typeof type !== 'function' && typeof type !== 'object')
  ) {
    return false;
  }
  return (
    'displayName' in type &&
    (type as { displayName?: string }).displayName === SLIDE_DISPLAY_NAME
  );
}

function isSlideElement(child: React.ReactElement): boolean {
  if (isCarouselItemType(child.type)) return true;
  const props = readSlideProps(child);
  return (
    props.basis !== undefined ||
    props['basis-md'] !== undefined ||
    props['basis-lg'] !== undefined
  );
}

/**
 * Slides this carousel will render, including a slide wrapped by a row.
 * A fragment is flattened. A row that is not itself a slide is opened.
 *
 * @param children - Carousel children
 * @returns Basis props for each slide, in order
 */
export function peekSlideProps(children: React.ReactNode): PeekSlideProps[] {
  const slides: PeekSlideProps[] = [];
  const visit = (node: React.ReactNode) => {
    React.Children.forEach(node, (child) => {
      if (!React.isValidElement(child)) return;
      if (isSlideElement(child)) {
        const props = readSlideProps(child);
        slides.push({
          basis: props.basis,
          'basis-md': props['basis-md'],
          'basis-lg': props['basis-lg'],
        });
        return;
      }
      const nested = readSlideProps(child).children;
      if (nested != null) visit(nested);
    });
  };
  visit(children);
  return slides;
}

function isFraction(value: unknown): value is FractionBasis {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(FRACTION_COUNT, value)
  );
}

function countAllowsPeek(token: unknown, slideCount: number): boolean {
  if (!isFraction(token)) return false;
  return slideCount > FRACTION_COUNT[token];
}

/** True when a summed row is wider than one page. `null` means a slide width is not a share of the row. */
export type FractionRowExceeds = {
  base: boolean | null;
  md: boolean | null;
  lg: boolean | null;
};

function resolvedBasis(
  slide: PeekSlideProps,
  breakpoint: 'base' | 'md' | 'lg',
): unknown {
  if (breakpoint === 'base') return slide.basis;
  if (breakpoint === 'md') {
    return slide['basis-md'] !== undefined ? slide['basis-md'] : slide.basis;
  }
  if (slide['basis-lg'] !== undefined) return slide['basis-lg'];
  if (slide['basis-md'] !== undefined) return slide['basis-md'];
  return slide.basis;
}

/**
 * Share of the row a token occupies. `half` is a full slide below md.
 * Cards and the 88% neighbor peek are not shares, so the caller keeps
 * the count rule for that breakpoint.
 *
 * @param token - Resolved basis at one breakpoint
 * @param breakpoint - Screen the share is for
 * @returns Fraction of one page, or null when the token is not a share
 */
function shareOfPage(
  token: unknown,
  breakpoint: 'base' | 'md' | 'lg',
): number | null {
  if (token === 'half' && breakpoint === 'base') return 1;
  if (token === 'full') return 1;
  if (isFraction(token)) return 1 / FRACTION_COUNT[token];
  return null;
}

/**
 * Whether fraction slides together need more than one page.
 * A half and two quarters are one page from md up, so the half must
 * not shrink on its own.
 *
 * @param slides - Basis props for every slide in the row
 * @returns Per-breakpoint overflow. `null` at a breakpoint we cannot sum
 */
export function fractionRowExceedsPage(
  slides: PeekSlideProps[],
): FractionRowExceeds {
  const at = (breakpoint: 'base' | 'md' | 'lg'): boolean | null => {
    if (slides.length === 0) return false;
    let sum = 0;
    for (const slide of slides) {
      const share = shareOfPage(resolvedBasis(slide, breakpoint), breakpoint);
      if (share == null) return null;
      sum += share;
    }
    return sum > 1 + 1e-9;
  };
  return { base: at('base'), md: at('md'), lg: at('lg') };
}

const PEEK_CLASS_AT = {
  base: BASE_PEEK_CLASS,
  md: MD_PEEK_CLASS,
  lg: LG_PEEK_CLASS,
} as const;

/** Authored fraction width, used to cancel a peek that leaked from a smaller screen. */
const AUTHORED_BASIS_AT: Record<
  'base' | 'md' | 'lg',
  Record<FractionBasis, string>
> = {
  base: {
    half: 'basis-full',
    third: 'basis-1/3',
    quarter: 'basis-1/4',
    fifth: 'basis-1/5',
  },
  md: {
    half: 'md:basis-1/2',
    third: 'md:basis-1/3',
    quarter: 'md:basis-1/4',
    fifth: 'md:basis-1/5',
  },
  lg: {
    half: 'lg:basis-1/2',
    third: 'lg:basis-1/3',
    quarter: 'lg:basis-1/4',
    fifth: 'lg:basis-1/5',
  },
};

/**
 * A fraction peeks at one breakpoint when the count allows it and the row
 * is wider than one page there. An unknown width keeps the count rule.
 * `half` is a full slide below md unless `peekOnPhone` is set. Then one
 * half peeks whenever another slide exists.
 *
 * @param token - Resolved basis at this breakpoint
 * @param breakpoint - Screen to decide
 * @param slideCount - Slides in this carousel
 * @param row - Summed overflow, omitted when the caller has no siblings
 * @param peekOnPhone - Let a half-width row peek below the tablet breakpoint
 * @returns Whether this slide should use the peek basis here
 */
function slidePeeks(
  token: unknown,
  breakpoint: 'base' | 'md' | 'lg',
  slideCount: number,
  row: FractionRowExceeds | undefined,
  peekOnPhone: boolean,
): boolean {
  if (token === 'half' && breakpoint === 'base') {
    if (!peekOnPhone || slideCount <= 1) return false;
    if (!row) return true;
    return row.base !== false;
  }
  if (!countAllowsPeek(token, slideCount)) return false;
  if (!row) return true;
  return row[breakpoint] !== false;
}

/**
 * Flex-basis overrides for fraction slides. One page of slides, full width,
 * fixed cards, and the 88% neighbor peek get nothing.
 *
 * @param input.basis - Slide width at the smallest screen
 * @param input.basisMd - Slide width from the md breakpoint
 * @param input.basisLg - Slide width from the lg breakpoint
 * @param input.active - False when peek is none or the carousel loops
 * @param input.slideCount - Slides in this carousel
 * @param input.peekOnPhone - Let a half-width row peek below the tablet breakpoint
 * @returns Space-separated classes, or an empty string
 */
export function carouselItemPeekClassName(input: {
  basis?: unknown;
  'basis-md'?: unknown;
  'basis-lg'?: unknown;
  active: boolean;
  slideCount: number;
  rowExceeds?: FractionRowExceeds;
  peekOnPhone?: boolean;
}): string {
  if (!input.active) return '';
  const peekOnPhone = input.peekOnPhone === true;
  const mdToken =
    input['basis-md'] !== undefined ? input['basis-md'] : input.basis;
  const tokens = {
    base: input.basis,
    md: mdToken,
    lg: input['basis-lg'] !== undefined ? input['basis-lg'] : mdToken,
  };
  const explicitAt = {
    base: false,
    md: input['basis-md'] !== undefined,
    lg: input['basis-lg'] !== undefined,
  };
  const classes: string[] = [];
  let calcActive: FractionBasis | null = null;
  let phoneHalfEmitted = false;
  for (const breakpoint of ['base', 'md', 'lg'] as const) {
    const token = tokens[breakpoint];
    const should = slidePeeks(
      token,
      breakpoint,
      input.slideCount,
      input.rowExceeds,
      peekOnPhone,
    );
    if (isFraction(token) && should) {
      const phoneHalf =
        peekOnPhone && token === 'half' && breakpoint === 'base';
      // An explicit breakpoint prop adds its own basis class, and that class
      // wins over a peek emitted at a smaller screen. Emit again so the peek
      // replaces it. A repeated fraction is still the same width. The phone
      // half class is one-up, so the tablet class has to be emitted after it.
      if (calcActive !== token || explicitAt[breakpoint] || phoneHalfEmitted) {
        classes.push(
          phoneHalf ? PHONE_HALF_PEEK_CLASS : PEEK_CLASS_AT[breakpoint][token],
        );
        calcActive = token;
      }
      phoneHalfEmitted = phoneHalf;
      continue;
    }
    if (calcActive && isFraction(token)) {
      classes.push(AUTHORED_BASIS_AT[breakpoint][token]);
    }
    calcActive = null;
    phoneHalfEmitted = false;
  }
  return Array.from(new Set(classes)).join(' ');
}

/**
 * Whether the peek is actually changing width at each breakpoint.
 * A later width can turn it off again. `half` is full width below md
 * unless `peekOnPhone` is set.
 *
 * @param input.basis - Slide width at the smallest screen
 * @param input.basisMd - Slide width from the md breakpoint
 * @param input.basisLg - Slide width from the lg breakpoint
 * @param input.slideCount - Slides in this carousel
 * @param input.peekOnPhone - Let a half-width row peek below the tablet breakpoint
 * @returns Peek state at the base, md, and lg breakpoints
 */
export function peekActiveAt(input: {
  basis?: unknown;
  'basis-md'?: unknown;
  'basis-lg'?: unknown;
  slideCount: number;
  rowExceeds?: FractionRowExceeds;
  peekOnPhone?: boolean;
}): { base: boolean; md: boolean; lg: boolean } {
  const peekOnPhone = input.peekOnPhone === true;
  const mdToken =
    input['basis-md'] !== undefined ? input['basis-md'] : input.basis;
  const lgToken = input['basis-lg'] !== undefined ? input['basis-lg'] : mdToken;
  const active = (token: unknown, breakpoint: 'base' | 'md' | 'lg') =>
    slidePeeks(
      token,
      breakpoint,
      input.slideCount,
      input.rowExceeds,
      peekOnPhone,
    );
  return {
    base: active(input.basis, 'base'),
    md: active(mdToken, 'md'),
    lg: active(lgToken, 'lg'),
  };
}
